/**
 * Game Lab welcome screen — the demo's front door.
 *
 * Built from the design in screen_welcome.png. The scenery is a generated background
 * plate (src/assets/screens/welcome_bg.png) with the UI drawn in real components on top,
 * rather than a flat screenshot: the buttons have to be pressable, the text has to scale,
 * and a bitmap of the whole screen would be soft on a high-DPI display.
 *
 * Only Create is wired up — it leads to the template picker. Play and Community are in
 * the design and are shown, because a single-button screen does not read as a product,
 * but they are visibly disabled rather than dead: a demo that silently does nothing on a
 * tap looks broken, whereas "coming soon" reads as scope.
 */

import React from "react";
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  ImageBackground,
  useWindowDimensions,
} from "react-native";
import { colors, textStyle } from "../styles";
import { useNavigation } from "../navigation/useNavigation";

import WELCOME_BG from "../assets/screens/welcome_bg.png";

interface Choice {
  id: string;
  label: string;
  sub: string;
  icon: string;
  color: string;
  available: boolean;
}

/** The three cards from the design, in the same order and colours. */
const CHOICES: Choice[] = [
  {
    id: "create",
    label: "Create",
    sub: "Build your own game",
    icon: "✨",
    color: "#EF4A55",
    available: true,
  },
  {
    id: "play",
    label: "Play",
    sub: "Play games & get ideas",
    icon: "🎮",
    color: "#7C3AED",
    available: false,
  },
  {
    id: "community",
    label: "Community",
    sub: "See what others made",
    icon: "👥",
    color: "#4CAF50",
    available: false,
  },
];

export default function GameLabScreen() {
  const { navigate } = useNavigation();
  const { width } = useWindowDimensions();
  // The design is a wide landscape layout; on a narrow window the cards would overflow,
  // so they shrink rather than clip.
  const cardWidth = Math.min(580, Math.max(280, width * 0.44));

  return (
    <ImageBackground source={WELCOME_BG} style={styles.root} resizeMode="cover">
      <View style={styles.center}>
        <Text style={styles.title} testID="gamelab-title">
          Game Lab
        </Text>
        <View style={styles.taglineRow}>
          <Text style={styles.tagline}>Imagine</Text>
          <View style={[styles.dot, { backgroundColor: "#7C3AED" }]} />
          <Text style={styles.tagline}>Create</Text>
          <View style={[styles.dot, { backgroundColor: "#EF4A55" }]} />
          <Text style={styles.tagline}>Play</Text>
        </View>

        <View style={styles.cards}>
          {CHOICES.map((c) => (
            <Pressable
              key={c.id}
              style={[
                styles.card,
                { backgroundColor: c.color, width: cardWidth },
                !c.available && styles.cardDisabled,
              ]}
              disabled={!c.available}
              onPress={() => navigate("gameTemplates", {})}
              testID={`gamelab-${c.id}`}
            >
              <View style={styles.iconCircle}>
                <Text style={styles.icon}>{c.icon}</Text>
              </View>
              <View style={styles.cardText}>
                <Text style={styles.cardLabel}>{c.label}</Text>
                <Text style={styles.cardSub}>
                  {c.available ? c.sub : "Coming soon"}
                </Text>
              </View>
              <Text style={styles.chevron}>{"›"}</Text>
            </Pressable>
          ))}
        </View>
      </View>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: "#6AC4FE" },
  center: { flex: 1, alignItems: "center", justifyContent: "center", padding: 20 },

  title: {
    fontSize: 68,
    lineHeight: 78,
    color: "#0F1B3D",
    ...textStyle.heading,
    textAlign: "center",
    // The design has a soft lift off the sky rather than a hard drop shadow.
    textShadowColor: "rgba(255,255,255,0.5)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 6,
  },
  taglineRow: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 22 },
  tagline: { fontSize: 22, color: "#1E2A4A", ...textStyle.body },
  dot: { width: 9, height: 9, borderRadius: 5 },

  cards: { gap: 14, alignItems: "center" },
  card: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    paddingVertical: 16,
    paddingHorizontal: 22,
    borderRadius: 26,
    // A darker under-edge, as in the design, so the cards read as chunky and tappable.
    borderBottomWidth: 5,
    borderBottomColor: "rgba(0,0,0,0.18)",
  },
  cardDisabled: { opacity: 0.62 },
  iconCircle: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: "rgba(255,255,255,0.22)",
    alignItems: "center",
    justifyContent: "center",
  },
  icon: { fontSize: 26 },
  cardText: { flex: 1 },
  cardLabel: { fontSize: 28, color: colors.white, ...textStyle.heading },
  cardSub: { fontSize: 15, color: "rgba(255,255,255,0.92)", ...textStyle.body },
  chevron: { fontSize: 34, color: colors.white, ...textStyle.heading },
});
